import {
  MESSAGE_AUDIENCES,
  MESSAGE_LOG_FILTERS,
  type MessageAudience,
  type MessageDeliveryState,
  type MessageLogFilter,
} from '@nest-recruit/shared';
import type { Tone } from '../../../../components/Tone';
import type { MessageLogParams } from '../../api/email-delivery-api';

// ADMIN Email delivery: what is in the address (06-qol §3, a copied link opens the same view), and the words and
// colours the page shares between its pieces.

export interface EmailDeliverySearch {
  readonly state?: MessageLogFilter;
  readonly q?: string;
  readonly audience?: MessageAudience;
  /** Asia/Dhaka days, `yyyy-mm-dd`. */
  readonly from?: string;
  readonly to?: string;
  readonly page?: number;
  /** The email open in the pane. */
  readonly open?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MAX_SEARCH = 120;

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return allowed.find((candidate) => candidate === value);
}

/** Anything odd in the address is dropped, never an error: the page opens on what it understood. */
export function readEmailDeliverySearch(raw: Readonly<Record<string, unknown>>): EmailDeliverySearch {
  const page = typeof raw.page === 'number' ? raw.page : Number(raw.page);
  const q = typeof raw.q === 'string' ? raw.q.trim().slice(0, MAX_SEARCH) : '';
  const state = oneOf(raw.state, MESSAGE_LOG_FILTERS);
  const audience = oneOf(raw.audience, MESSAGE_AUDIENCES);
  return {
    ...(state ? { state } : {}),
    ...(q !== '' ? { q } : {}),
    ...(audience ? { audience } : {}),
    ...(typeof raw.from === 'string' && DAY.test(raw.from) ? { from: raw.from } : {}),
    ...(typeof raw.to === 'string' && DAY.test(raw.to) ? { to: raw.to } : {}),
    ...(Number.isInteger(page) && page > 1 ? { page } : {}),
    ...(typeof raw.open === 'string' && UUID.test(raw.open) ? { open: raw.open } : {}),
  };
}

export const EMAIL_PAGE_SIZE = 30;

export function logParamsOf(search: EmailDeliverySearch): MessageLogParams {
  return {
    page: search.page ?? 1,
    ...(search.state ? { state: search.state } : {}),
    ...(search.q ? { q: search.q } : {}),
    ...(search.audience ? { audience: search.audience } : {}),
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
  };
}

/** How many filters other than the pill are set, for "Clear filters". */
export function filterCountOf(search: EmailDeliverySearch): number {
  return [search.q, search.audience, search.from ?? search.to].filter((value) => value !== undefined).length;
}

export const STATE_TONES: Readonly<Record<MessageDeliveryState, Tone>> = {
  attention: 'urgent',
  dismissed: 'ended',
  replaced: 'ended',
  retrying: 'attention',
  queued: 'new',
  scheduled: 'new',
  sent: 'progress',
  cancelled: 'ended',
};

export const FILTER_TONES: Readonly<Record<MessageLogFilter, Tone>> = {
  attention: 'urgent',
  retrying: 'attention',
  queued: 'new',
  scheduled: 'new',
  sent: 'progress',
  failed: 'urgent',
  cancelled: 'ended',
};

export function plural(count: number, one: string, many: string): string {
  return `${count.toLocaleString()} ${count === 1 ? one : many}`;
}

/** "Interview booked (N-28), to the candidate". */
export function messageLine(row: { readonly messageName: string | null; readonly catalogueId: string | null; readonly templateKey: string | null; readonly audience: MessageAudience }): string {
  const name = row.messageName ?? row.templateKey ?? 'A message someone wrote';
  const code = row.catalogueId ? ` (${row.catalogueId})` : '';
  return `${name}${code}, ${row.audience === 'candidates' ? 'to the candidate' : 'to staff'}`;
}
